import '../css/style.css';
import homeHtml from '../html/pages/home.html?raw';
import aboutHtml from '../html/pages/about.html?raw';
import contactHtml from '../html/pages/contact.html?raw';
import boardHtml from '../html/pages/board.html?raw';
import faqHtml from '../html/pages/faq.html?raw';
import schoolHtml from '../html/pages/school.html?raw';
import seasonHtml from '../html/pages/season.html?raw';
import careHtml from '../html/pages/care.html?raw';
import legalHtml from '../html/pages/legal.html?raw';
import notFoundHtml from '../html/pages/not-found.html?raw';
import { applyBaseToHtml, withBase } from './base';
import { createRouter, type PageId, type RouteMatch } from './router';
import { initBrandSlider, initServiceCarousel } from './carousel';
import { initMenu } from './menu';
import { initHeaderScroll } from './header-scroll';
import { initHeroViewport } from './hero-viewport';
import { initHeroSlider } from './hero-slider';
import { initHistoryTabs } from './history-tabs';
import { initSchoolTabs, setSchoolTab, type SchoolTab } from './school-tabs';
import { initSeasonScrollSpy } from './season-scroll';
import { initBoardPage, initHomePosts } from './boards';
import { initInquiryForm } from './inquiry';
import { initLegalPage } from './legal';

const appElement = document.querySelector<HTMLElement>('#app');

if (!appElement) {
  throw new Error('#app 요소를 찾을 수 없습니다.');
}

const app: HTMLElement = appElement;

let cleanupFns: Array<() => void> = [];

function teardown() {
  cleanupFns.forEach((fn) => fn());
  cleanupFns = [];
}

function mount(html: string) {
  app.innerHTML = applyBaseToHtml(html);
}

function initSharedChrome(root: HTMLElement) {
  cleanupFns.push(initMenu(root));

  const header = root.querySelector<HTMLElement>('[data-header]');
  if (header) {
    cleanupFns.push(initHeaderScroll(header));
  }
}

function initHomeInteractions(root: HTMLElement) {
  initSharedChrome(root);

  const hero = root.querySelector<HTMLElement>('.hero');
  if (hero) {
    cleanupFns.push(initHeroViewport(hero));
    cleanupFns.push(initHeroSlider(hero));
  }

  const serviceTrack = root.querySelector<HTMLElement>('[data-service-track]');
  if (serviceTrack) {
    cleanupFns.push(
      initServiceCarousel({
        track: serviceTrack,
        prevButton: root.querySelector<HTMLButtonElement>('[data-service-prev]'),
        nextButton: root.querySelector<HTMLButtonElement>('[data-service-next]'),
        itemSelector: '.service__card',
        gap: 30,
      }),
    );
  }

  cleanupFns.push(
    initBrandSlider(root, [
      {
        name: '구멍떡볶이, 구멍가래떡',
        description: '떡 사이에 구멍을 내어 양념과 소스가 잘 어우러지게 조리',
        image: withBase('/assets/images/our-product1.jpg'),
        visual: withBase('/assets/images/our-product-visual1.jpg'),
        alt: '구멍떡볶이, 구멍가래떡',
      },
      {
        name: '딸기쨈 백설기',
        description: '알록달록한 색감으로 아이들의 식욕을 돋우는 급식 인기 제품',
        image: withBase('/assets/images/our-product2.jpg'),
        visual: withBase('/assets/images/our-product-visual2.jpg'),
        alt: '색동떡볶이, 색동조랭이',
      },
      {
        name: '삼색 꿀떡',
        description: '부드러운 식감과 은은한 달콤함으로 아이들이 좋아하는 후식 떡',
        image: withBase('/assets/images/our-product3.jpg'),
        visual: withBase('/assets/images/our-product-visual3.jpg'),
        alt: '꿀떡',
      },
      {
        name: '찹살새알심',
        description: '정성스럽게 찐 보슬보슬한 식감의 학교급식 베스트 설기떡',
        image: withBase('/assets/images/our-product4.jpg'),
        visual: withBase('/assets/images/our-product-visual4.jpg'),
        alt: '백설기',
      },
    ]),
  );

  cleanupFns.push(initHomePosts(root));
}

function initAboutInteractions(root: HTMLElement) {
  initSharedChrome(root);
  cleanupFns.push(initHistoryTabs(root));
}

function renderHome() {
  mount(homeHtml);
  initHomeInteractions(app);
  window.scrollTo(0, 0);
}

function renderAbout() {
  mount(aboutHtml);
  initAboutInteractions(app);
  window.scrollTo(0, 0);
}

function renderContact() {
  mount(contactHtml);
  initSharedChrome(app);
  cleanupFns.push(initInquiryForm(app));
  window.scrollTo(0, 0);
}

function renderArchive(postId?: string) {
  mount(boardHtml);
  initSharedChrome(app);
  cleanupFns.push(initBoardPage(app, 'archive', postId));
  if (!postId) window.scrollTo(0, 0);
}

function renderFaq(postId?: string) {
  mount(faqHtml);
  initSharedChrome(app);
  cleanupFns.push(initBoardPage(app, 'faq', postId));
  if (!postId) window.scrollTo(0, 0);
}

function renderLegal(slot: 'site_privacy' | 'site_service') {
  mount(legalHtml);
  initSharedChrome(app);
  cleanupFns.push(initLegalPage(app, slot));
  window.scrollTo(0, 0);
}

function schoolTabFromPage(page: PageId): SchoolTab | null {
  if (page === 'school') return 'cooking';
  if (page === 'schoolSnack') return 'snack';
  return null;
}

function renderSchool(tab: SchoolTab) {
  mount(schoolHtml);
  initSharedChrome(app);
  cleanupFns.push(initSchoolTabs(app, tab));
  window.scrollTo(0, 0);
}

function renderSeason() {
  mount(seasonHtml);
  initSharedChrome(app);
  cleanupFns.push(initSeasonScrollSpy(app));
  window.scrollTo(0, 0);
}

function renderCare() {
  mount(careHtml);
  initSharedChrome(app);
  window.scrollTo(0, 0);
}

function renderNotFound() {
  mount(notFoundHtml);
  window.scrollTo(0, 0);
}

async function handleRoute(match: RouteMatch) {
  const schoolTab = schoolTabFromPage(match.page);

  // 학교급식 페이지 안에서의 URL/메뉴 이동: 패널만 바꾸고 상단으로
  if (schoolTab && app.querySelector('[data-school-page]')) {
    setSchoolTab(app, schoolTab);
    window.scrollTo(0, 0);
    return;
  }

  teardown();

  switch (match.page) {
    case 'home':
      renderHome();
      break;
    case 'about':
      renderAbout();
      break;
    case 'contact':
      renderContact();
      break;
    case 'archive':
      renderArchive(match.params.id);
      break;
    case 'faq':
      renderFaq(match.params.id);
      break;
    case 'school':
      renderSchool('cooking');
      break;
    case 'schoolSnack':
      renderSchool('snack');
      break;
    case 'season':
      renderSeason();
      break;
    case 'care':
      renderCare();
      break;
    case 'privacy':
      renderLegal('site_privacy');
      break;
    case 'terms':
      renderLegal('site_service');
      break;
    case 'notFound':
      renderNotFound();
      break;
    default:
      renderNotFound();
  }
}

createRouter(handleRoute);
